import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Collapse,
} from '@mui/material';
import {
  PlayArrow as ExecuteIcon,
  AttachFile as AttachIcon,
  Delete as RemoveIcon,
  Visibility as PreviewIcon,
  VisibilityOff as HidePreviewIcon,
} from '@mui/icons-material';
import Mustache from 'mustache';
import { CodeEditor } from '../components/common/CodeEditor';
import { agentFunctionService } from '../services/agentFunctionService';
import { useNotificationStore } from '../store/notificationStore';
import type { AgentFunctionDto, AgentFunctionRequestDto } from '../types/agentFunction';
import { AgentFunctionRequestStatus } from '../types/agentFunction';
import { ROUTES } from '../utils/routes';

/**
 * Execute Agent Function page.
 * Select an agent function by slug, provide input JSON and optionally a file, execute, and view the result.
 */
export const AgentFunctionExecutePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const slugFromUrl = searchParams.get('slug');
  const { addNotification } = useNotificationStore();

  const [functions, setFunctions] = useState<AgentFunctionDto[]>([]);
  const [selectedFunction, setSelectedFunction] = useState<AgentFunctionDto | null>(null);
  const [description, setDescription] = useState<string>('');
  const [systemPromptTemplate, setSystemPromptTemplate] = useState<string>('');
  const [inputJson, setInputJson] = useState<string>('{}');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [loadingFunctions, setLoadingFunctions] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<AgentFunctionRequestDto | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Load active agent functions
  useEffect(() => {
    const load = async () => {
      setLoadingFunctions(true);
      try {
        const res = await agentFunctionService.getAgentFunctions({
          isActive: true,
          pageSize: 100,
        });
        setFunctions(res.items);
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to load agent functions';
        addNotification(msg, 'error');
      } finally {
        setLoadingFunctions(false);
      }
    };
    load();
  }, [addNotification]);

  // Pre-select function from URL ?slug=xxx
  useEffect(() => {
    if (!slugFromUrl || functions.length === 0) return;
    const match = functions.find((f) => f.slug === slugFromUrl);
    if (match) {
      setSelectedFunction(match);
    }
  }, [slugFromUrl, functions]);

  // When function is selected, fetch detail and pre-fill input JSON
  useEffect(() => {
    if (!selectedFunction) {
      setDescription('');
      setSystemPromptTemplate('');
      setInputJson('{}');
      setResult(null);
      return;
    }

    const load = async () => {
      try {
        const detail = await agentFunctionService.getAgentFunctionBySlug(selectedFunction.slug);
        setDescription(detail.description ?? '');
        setSystemPromptTemplate(detail.systemPrompt ?? '');
        const template = detail.inputSchemaJson?.trim() || '{}';
        try {
          const parsed = JSON.parse(template);
          setInputJson(JSON.stringify(parsed, null, 2));
        } catch {
          setInputJson(template);
        }
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to load function details';
        addNotification(msg, 'error');
      }
    };

    load();
    setResult(null);
  }, [selectedFunction, addNotification]);

  const handleFunctionChange = (_event: unknown, value: AgentFunctionDto | null) => {
    setSelectedFunction(value);
    if (value?.slug) {
      setSearchParams({ slug: value.slug });
    } else {
      searchParams.delete('slug');
      setSearchParams(searchParams);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setAttachedFile(file ?? null);
  };

  const handleRemoveFile = () => {
    setAttachedFile(null);
    setFileInputKey((k) => k + 1);
  };

  const handleExecute = async () => {
    if (!selectedFunction) return;

    setExecuting(true);
    setResult(null);
    try {
      const res = await agentFunctionService.executeAgentFunction(
        selectedFunction.slug,
        inputJson,
        attachedFile ?? undefined
      );
      setResult(res);
      addNotification('Function executed successfully', 'success');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Execution failed';
      addNotification(msg, 'error');
      setResult({
        id: '',
        agentFunctionId: selectedFunction.id,
        agentFunctionName: selectedFunction.name,
        agentFunctionSlug: selectedFunction.slug,
        inputJson,
        status: AgentFunctionRequestStatus.Failed,
        errorMessage: msg,
        createdAt: new Date().toISOString(),
      });
    } finally {
      setExecuting(false);
    }
  };

  // Live-resolve Mustache variables in the system prompt using input JSON values
  const resolvedPromptPreview = useMemo(() => {
    if (!systemPromptTemplate) return '';
    try {
      const inputData = JSON.parse(inputJson || '{}');
      return Mustache.render(systemPromptTemplate, inputData);
    } catch {
      return systemPromptTemplate;
    }
  }, [systemPromptTemplate, inputJson]);

  // Check if the system prompt has Mustache variables
  const hasMustacheVariables = useMemo(() => {
    return /\{\{[#/^]?[a-zA-Z_][a-zA-Z0-9_.]*\}\}/.test(systemPromptTemplate);
  }, [systemPromptTemplate]);

  const statusLabel =
    result?.status === AgentFunctionRequestStatus.Completed
      ? 'Completed'
      : result?.status === AgentFunctionRequestStatus.Failed
        ? 'Failed'
        : result?.status === AgentFunctionRequestStatus.Processing
          ? 'Processing'
          : 'Pending';
  const statusColor =
    result?.status === AgentFunctionRequestStatus.Completed
      ? 'success'
      : result?.status === AgentFunctionRequestStatus.Failed
        ? 'error'
        : 'default';

  return (
    <Box sx={{ p: 4, maxWidth: 960, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
        Execute Agent Function
      </Typography>

      {/* Slug selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
            Agent Function
          </Typography>
          <Autocomplete
            options={functions}
            getOptionLabel={(opt) => `${opt.name} (${opt.slug})`}
            value={selectedFunction}
            onChange={handleFunctionChange}
            loading={loadingFunctions}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select function by name or slug"
                placeholder="Search..."
                size="small"
              />
            )}
            sx={{ mt: 1 }}
          />
          {description && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* Input JSON editor */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
            Input JSON
          </Typography>
          <Box sx={{ mt: 1 }}>
            <CodeEditor
              value={inputJson}
              onChange={setInputJson}
              language="json"
              height="240px"
              readOnly={false}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Resolved Prompt Preview */}
      {systemPromptTemplate && hasMustacheVariables && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
                Resolved System Prompt Preview
              </Typography>
              <Button
                size="small"
                startIcon={showPreview ? <HidePreviewIcon /> : <PreviewIcon />}
                onClick={() => setShowPreview((v) => !v)}
              >
                {showPreview ? 'Hide' : 'Show'} Preview
              </Button>
            </Box>
            <Collapse in={showPreview}>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  This preview shows how the system prompt will look after Mustache variable substitution with the current input values.
                </Typography>
                <CodeEditor
                  value={resolvedPromptPreview}
                  language="plaintext"
                  height="180px"
                  readOnly
                />
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* File attachment */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
            File Attachment
          </Typography>
          <Box
            sx={{
              mt: 1,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: 'action.hover',
            }}
          >
            <input
              key={fileInputKey}
              type="file"
              id="agent-function-file"
              hidden
              onChange={handleFileChange}
              accept="*/*"
            />
            <Button
              component="label"
              htmlFor="agent-function-file"
              variant="outlined"
              startIcon={<AttachIcon />}
              sx={{ mr: 2 }}
            >
              Attach File
            </Button>
            {attachedFile ? (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.primary">
                  {attachedFile.name}
                </Typography>
                <IconButton size="small" onClick={handleRemoveFile} color="error" aria-label="Remove file">
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No file selected
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Execute button */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={executing ? <CircularProgress size={20} color="inherit" /> : <ExecuteIcon />}
          onClick={handleExecute}
          disabled={!selectedFunction || executing}
        >
          {executing ? 'Executing...' : 'Execute'}
        </Button>
      </Box>

      {/* Result panel */}
      {result && (
        <Card>
          <CardContent>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
              Result
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <Chip label={statusLabel} color={statusColor} size="small" />
              {result.durationMs != null && (
                <Typography variant="body2" color="text.secondary">
                  {result.durationMs} ms
                </Typography>
              )}
            </Box>

            {result.status === AgentFunctionRequestStatus.Failed && result.errorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {result.errorMessage}
              </Alert>
            )}

            {(result.outputJson != null && result.outputJson !== '') && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Output JSON
                </Typography>
                <CodeEditor
                  value={result.outputJson ?? '{}'}
                  language="json"
                  height="300px"
                  readOnly
                />
              </Box>
            )}

            {result.id && (
              <Box sx={{ mt: 2 }}>
                <Link
                  component={RouterLink}
                  to={ROUTES.AGENT_FUNCTION_REQUEST_DETAIL.replace(':requestId', result.id)}
                  variant="body2"
                >
                  View full request detail
                </Link>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
